import React, { useState, useEffect } from 'react';
import { useUser } from '../../contexts/UserContext';
import { getUsageStats } from '../../services/api';

function UsageDashboard() {
  const { user, loading: userLoading } = useUser();
  const [usageData, setUsageData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchUsageStats = async () => {
      try {
        setLoading(true);
        const data = await getUsageStats();
        setUsageData(data);
        setError(null);
      } catch (err) {
        console.error('Failed to fetch usage stats:', err);
        setError('Failed to load usage data. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    if (user) {
      fetchUsageStats();
    }
  }, [user]);

  if (userLoading || loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <p className="text-muted">Loading usage data...</p>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <p className="text-muted">Please log in to view your usage.</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <p className="text-red-400">{error}</p>
      </div>
    );
  }

  const getProgressBarColor = (percentage) => {
    if (percentage >= 90) return 'bg-red-500';
    if (percentage >= 75) return 'bg-yellow-500';
    return 'bg-brand-blue';
  };

  const formatLimit = (limit) => {
    if (limit === -1) return 'Unlimited';
    return limit.toLocaleString();
  };

  const renderUsageBar = (label, data, description) => {
    const isUnlimited = data.limit === -1;
    const percentage = isUnlimited ? 0 : data.percentage;

    return (
      <div className="bg-gray-800/50 rounded-lg p-4 mb-4">
        <div className="flex justify-between items-center mb-2">
          <div>
            <h3 className="text-brand-white font-medium">{label}</h3>
            <p className="text-sm text-muted">{description}</p>
          </div>
          <div className="text-right">
            <span className="text-brand-white font-semibold">
              {data.used.toLocaleString()}
            </span>
            <span className="text-muted"> / {formatLimit(data.limit)}</span>
          </div>
        </div>
        {!isUnlimited && (
          <div className="w-full bg-gray-700 rounded-full h-2.5">
            <div
              className={`h-2.5 rounded-full transition-all duration-300 ${getProgressBarColor(percentage)}`}
              style={{ width: `${Math.min(percentage, 100)}%` }}
            ></div>
          </div>
        )}
        {!isUnlimited && percentage >= 90 && (
          <p className="text-sm text-yellow-400 mt-2">
            You've used {percentage}% of your limit
          </p>
        )}
        {isUnlimited && (
          <p className="text-sm text-green-400 mt-2">
            Unlimited usage
          </p>
        )}
      </div>
    );
  };

  return (
    <div className="max-w-2xl mx-auto py-8">
      <h1 className="text-2xl font-bold text-brand-white mb-2">Usage Dashboard</h1>
      <p className="text-muted mb-8">
        Track your usage for {usageData?.current_period || 'this month'}
      </p>

      {/* Subscription Tier */}
      <section className="bg-surface rounded-lg border border-muted p-6 mb-6">
        <div className="flex justify-between items-center">
          <div>
            <h2 className="text-lg font-semibold text-brand-white">Current Plan</h2>
            <p className="text-muted">Your subscription tier</p>
          </div>
          <span className="px-4 py-2 bg-brand-blue/20 text-brand-blue font-semibold rounded-lg">
            {usageData?.tier_display || 'Free'}
          </span>
        </div>
      </section>

      {/* Cancellation Warning */}
      {usageData?.cancel_at_period_end && usageData?.subscription_period_end && (
        <section className="bg-yellow-900/20 border border-yellow-600 rounded-lg p-4 mb-6">
          <div className="flex items-center gap-2 mb-2">
            <span className="text-yellow-400 text-xl">&#9888;</span>
            <h3 className="text-yellow-400 font-semibold">Subscription Ending</h3>
          </div>
          <p className="text-yellow-300">
            Your <strong>{usageData.tier_display}</strong> plan will end on{' '}
            <strong>{new Date(usageData.subscription_period_end).toLocaleDateString('en-US', {
              year: 'numeric',
              month: 'long',
              day: 'numeric'
            })}</strong>
          </p>
          <p className="text-yellow-300/80 text-sm mt-1">
            You'll continue to have {usageData.tier_display} access until then.
          </p>
        </section>
      )}

      {/* Warnings Section */}
      {usageData?.warnings && usageData.warnings.length > 0 && (
        <section className="bg-yellow-900/20 border border-yellow-600 rounded-lg p-4 mb-6">
          <h3 className="text-yellow-400 font-semibold mb-2">Usage Warnings</h3>
          {usageData.warnings.map((warning, index) => (
            <p key={index} className="text-yellow-300 text-sm">
              {warning.message}
            </p>
          ))}
        </section>
      )}

      {/* Monthly Usage Section */}
      <section className="bg-surface rounded-lg border border-muted p-6 mb-6">
        <h2 className="text-lg font-semibold text-brand-white mb-4">Monthly Usage</h2>
        <p className="text-sm text-muted mb-4">Resets at the start of each month</p>

        {usageData?.screener_contracts && renderUsageBar(
          'Screener Contracts',
          usageData.screener_contracts,
          'Contracts returned from screener runs'
        )}

        {usageData?.watchlist_contracts && renderUsageBar(
          'Watchlist Contracts',
          usageData.watchlist_contracts,
          'Contracts simulated in watchlist'
        )}
      </section>

      {/* Hard Caps Section */}
      <section className="bg-surface rounded-lg border border-muted p-6">
        <h2 className="text-lg font-semibold text-brand-white mb-4">Saved Items</h2>
        <p className="text-sm text-muted mb-4">Total items currently saved</p>

        {usageData?.saved_contracts && renderUsageBar(
          'Saved Contracts',
          usageData.saved_contracts,
          'Contracts saved in watchlist groups'
        )}

        {usageData?.saved_parameters && renderUsageBar(
          'Saved Parameters',
          usageData.saved_parameters,
          'Screener configurations saved'
        )}
      </section>

      {/* Upgrade CTA */}
      {usageData?.tier !== 'premium' && (
        <section className="mt-6 text-center">
          <p className="text-muted mb-2">Need more usage?</p>
          <button className="px-6 py-2 bg-brand-blue hover:bg-brand-blue/80 text-white rounded-lg transition">
            Upgrade Plan
          </button>
        </section>
      )}
    </div>
  );
}

export default UsageDashboard;
